import { Fragment, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

interface BreadcrumbItemType {
  label: string;
  href?: string; // undefined = current page
}

interface SEOBreadcrumbProps {
  items: BreadcrumbItemType[];
  align?: 'left' | 'center';
}

export const SEOBreadcrumb = ({ items, align = 'center' }: SEOBreadcrumbProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  
  // Generate Schema.org BreadcrumbList JSON-LD
  useEffect(() => {
    const origin = window.location.origin;
    
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": t('common.home'),
          "item": origin
        },
        ...items.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 2,
          "name": item.label,
          ...(item.href ? { "item": `${origin}${item.href}` } : {})
        }))
      ]
    };

    // Create or update the JSON-LD script
    const scriptId = 'breadcrumb-jsonld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(schemaData);

    // Cleanup on unmount
    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [items, t, location.pathname]);

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList className={`text-sm ${align === 'center' ? 'justify-center' : ''}`}>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link 
              to="/" 
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              {t('common.home')}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        
        {items.map((item, index) => (
          <Fragment key={index}>
            <BreadcrumbSeparator className="text-muted-foreground/50" />
            <BreadcrumbItem>
              {item.href ? (
                <BreadcrumbLink asChild>
                  <Link 
                    to={item.href} 
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {item.label}
                  </Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage className="text-foreground font-medium truncate max-w-[200px] md:max-w-none">
                  {item.label}
                </BreadcrumbPage>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
